import { useAuth } from '../auth/AuthContext';
import { useSettings } from '../settings/SettingsContext';
import { useEntitlements } from '../subscription/offers';
import { useAiAccess } from '../ai/useAiAccess';
import { actingFor } from '../api/client';
import { useAssistAccess } from '../assist/useAssistAccess';

export type IconName = 'home' | 'library' | 'plan' | 'sets' | 'report' | 'talk' | 'studio' | 'assist' | 'games';

export type PrimaryLink = { to: string; label: string; icon: IconName };

export function usePrimaryLinks(): PrimaryLink[] {
  const { user } = useAuth();
  const { settings } = useSettings();
  const entitlements = useEntitlements(!!user);
  const aiVisible = !!useAiAccess().access?.visible;
  const expertStatus = useAssistAccess()?.expertStatus ?? null;
  if (!user) return [];
  const mainApp = entitlements?.main !== false;
  const acting = actingFor();
  const links: (PrimaryLink | false)[] = [
    mainApp ? { to: '/', label: 'Home', icon: 'home' } : !entitlements?.talkBoard && { to: '/subscribe', label: 'Subscribe', icon: 'home' },
    mainApp && { to: '/library', label: 'Library', icon: 'library' },
    mainApp && settings.features.games && { to: '/games', label: 'Games', icon: 'games' },
    mainApp && { to: '/plan', label: 'Plan', icon: 'plan' },
    mainApp && settings.features.programPackages && { to: '/packages', label: 'Sets', icon: 'sets' },
    mainApp && settings.features.reportExport && { to: '/reports', label: 'Report', icon: 'report' },
    !!entitlements?.talkBoard && !acting && { to: '/talk', label: 'Talk Board', icon: 'talk' },
    mainApp && aiVisible && !acting && { to: '/studio', label: 'Studio', icon: 'studio' },
    mainApp && !acting && { to: '/assist', label: 'Assist', icon: 'assist' },
    !mainApp && !acting && !!expertStatus && { to: '/assist/expert', label: 'Work as a consultant', icon: 'assist' },
  ];
  return links.filter((l): l is PrimaryLink => !!l);
}

const PROGRAMS = ['/library', '/games', '/packages', '/studio'];

export function groupLinks(links: PrimaryLink[]): (PrimaryLink | { group: PrimaryLink[] })[] {
  const members = links.filter((l) => PROGRAMS.includes(l.to));
  if (links.length <= 5 || members.length < 2) return links;
  const out: (PrimaryLink | { group: PrimaryLink[] })[] = [];
  for (const l of links) {
    if (!PROGRAMS.includes(l.to)) out.push(l);
    else if (l === members[0]) out.push({ group: members });
  }
  return out;
}
